// Lifecycle date semantics — three user-facing dates, one rule each:
//
//   start   ("Start")    — a movable PLAN while draft/not-started; becomes a
//                          frozen FACT on the first move into in-progress.
//                          The transition asks "when did work start?" (backdatable,
//                          defaulting to the planned start); if no date is
//                          provided, today is stamped. Corrections after the
//                          freeze happen in the drawer only — never by drag.
//   target  ("Target")   — when we THINK it'll be done. Free to move until
//                          shipped (the living forecast); a copy is frozen as
//                          originalTarget the moment work starts, so the retro
//                          always compares against the estimate made at build time.
//   shipped ("Shipped")  — the day it actually went out. The move into shipped
//                          asks "when did it ship?" (backdatable); if no date
//                          is provided, today is stamped. Correctable in the drawer.
//
// The retro is then just: shipped vs (originalTarget ?? target) — "we thought
// Feb 6, it shipped Mar 2, 24 days late — why?". Write-once stamps: bouncing
// back to not-started and starting again does NOT re-stamp.

import type { Initiative, Status } from "./types";
import { TODAY } from "./constants";

const COMMITTED: readonly Status[] = ["in-progress", "shipped"];

export function isCommittedStatus(s: Status): boolean {
  return COMMITTED.includes(s);
}

function todayTuple(): [number, number, number] {
  const d = new Date();
  return [d.getFullYear(), d.getMonth(), d.getDate()];
}

// The ship date only counts when the status is Shipped. A date left on a
// row in any other status (typed into the field, or kept after reopening)
// is ignored: it must not lock the bar, end it, or skew the retro.
export function actualShipped(i: Pick<Initiative, "status" | "shipped">): [number, number, number] | undefined {
  return i.status === "shipped" ? i.shipped : undefined;
}

// For create(): stamps for initiatives born directly into a committed state.
export function lifecycleAtCreate(data: Omit<Initiative, "id" | "sortOrder">): Partial<Initiative> {
  if (!isCommittedStatus(data.status)) return {};
  const out: Partial<Initiative> = { originalTarget: data.target };
  if (!data.start) out.start = todayTuple();
  if (data.status === "shipped" && !data.shipped) out.shipped = todayTuple();
  return out;
}

// For update()/postUpdate(): returns the patch with any one-time stamps this
// status change triggers. Never overwrites a date that already exists or that
// the patch itself provides. Stamps fire only on an actual TRANSITION into a
// committed state — a drawer save that re-sends an unchanged in-progress
// status (every edit does) must not re-run stamp logic, or a typo fix on a
// legacy row would silently freeze today's target as the baseline.
export function withLifecycleStamps(existing: Initiative, patch: Partial<Initiative>): Partial<Initiative> {
  if (!patch.status || patch.status === existing.status) return patch;
  const out = { ...patch };
  // Reopening a shipped item: the ship date is no longer a fact — clear it
  // (unless the caller explicitly provided one).
  if (existing.status === "shipped" && out.status !== "shipped" && !("shipped" in patch)) out.shipped = undefined;
  if (!isCommittedStatus(out.status!)) return out;
  if (!existing.originalTarget && !out.originalTarget) out.originalTarget = out.target ?? existing.target;
  if (!existing.start && !out.start) out.start = todayTuple();
  if (out.status === "shipped" && !actualShipped(existing) && !out.shipped) out.shipped = todayTuple();
  return out;
}

// Overdue: the target date has passed and the work isn't done. Not a status —
// it's derived, so it can't drift. Shipped and Canceled are never overdue, and
// neither is On Hold: a pause is a deliberate call, so it keeps its own color.
// Rows without a target date can't be overdue.
const NEVER_OVERDUE: readonly Status[] = ["shipped", "canceled", "on-hold"];

export function isOverdue(i: Pick<Initiative, "status" | "target">, today: Date = TODAY): boolean {
  if (NEVER_OVERDUE.includes(i.status) || !i.target) return false;
  return new Date(i.target[0], i.target[1], i.target[2]) < today;
}

// Retro outcome: whole-day delta between actual ship and the at-start estimate.
// Positive = late, negative = early, 0 = on target. Null until shipped or
// when there was never an estimate.
export function shipDeltaDays(i: Initiative): number | null {
  const anchor = i.originalTarget ?? i.target;
  const shipped = actualShipped(i);
  if (!shipped || !anchor) return null;
  const a = new Date(anchor[0], anchor[1], anchor[2]);
  const s = new Date(shipped[0], shipped[1], shipped[2]);
  return Math.round((s.getTime() - a.getTime()) / 86400000);
}
